import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import {
	ErrorResponse,
	InfiniteData,
	InfinitivePage,
	SuccessCursorResponse,
	SuccessPageResponse,
	SuccessResponse,
	useHttpClient,
} from "api/api"
import { Device, DeviceStatusHistory } from "interface/device"
import {
	InfinitePatientData,
	NurseTime,
	Patient,
	PatientDetailItem,
	PatientDeviceDataStats,
	PatientDiagnosisCode,
	PatientItem,
	PatientLatestScan,
	ProfileNote,
	SuccessPageResponsePatientData,
	TimelineItem,
	TimelineNote,
	TimelineNotePayload,
	TimelineScreening,
} from "interface/patientType"
import {
	TemplatedFormParams,
	TemplatedFormQuestion,
} from "interface/templatedForm"
import { useInvalidateOnRealtimeEvent } from "modules/realtime/useInvalidateOnRealtimeEvent"
import { useMemo } from "react"
import { useParams } from "react-router"
import { asDate, today } from "utils/date"

export const PAGE_SIZE = 20

interface DiagnosisCodeMutationContext {
	previousDiagnosisCodes?: PatientDiagnosisCode[]
}

export const OPTIMISTIC_ID_PREFIX = "optimistic:"

export const queryKeys = {
	organizations: "organizations",
	patients: "patients",
	patientDetails: "patient-details",
	patientData: "patient-data",
	patientScreening: "patient-screening",
	patientScreeninsStats: "patient-screenings-stats",
	patientDevice: "patient-device",
	latestScan: "patient-latest-scan",
	nurseTimes: "patient-nurse-times",
	nurseTimeCreate: "patient-nurse-time-create",
	nurseTimeUpdate: "patient-nurse-time-update",
	nurseTimeDelete: "patient-nurse-time-delete",
	patientDeviceStatusHistory: "patient-device-status-history",
	profileNote: "patient-profile-note",
	profileNoteUpdate: "patient-profile-note-update",
	profileNotePinMessage: "patient-profile-note-pin-message",
	profileNoteUnpinMessage: "patient-profile-note-unpin-message",
	timeline: "patient-timeline",
	timelineScreenings: "patient-timeline-screenings",
	timelineNoteCreate: "patient-timeline-note-create",
	timelineNoteUpdate: "patient-timeline-note-update",
	timelineNoteDelete: "patient-timeline-note-delete",
	patientDiagnosisCodes: "patient-diagnosis-codes",
	patientDiagnosisCodeAdd: "patient-diagnosis-code-add",
	patientDiagnosisCodeRemove: "patient-diagnosis-code-remove",
}

function hasPatient(query: any, patientId?: string) {
	if (!patientId) {
		return false
	}
	const infinitePatients = query.state.data as
		InfiniteData<PatientItem> | undefined
	const hasPatient = infinitePatients?.pages.some((page) =>
		page.data?.some(({ id }) => id === patientId),
	)
	return !!hasPatient
}

export const usePatients = (search?: string) => {
	const { httpClient } = useHttpClient()
	const { scopeId } = useParams()
	const queryKey = useMemo(
		() => [queryKeys.patients, scopeId, search],
		[scopeId, search],
	)

	useInvalidateOnRealtimeEvent(
		[
			"unread_count_update",
			"ticket_updated",
			"ticket_deleted",
			"escalation_updated",
		],
		{
			queryKey,
			queryPredicate: (query, data) => hasPatient(query, data.patientId),
		},
	)

	return useInfiniteQuery({
		queryKey,
		queryFn: async ({ pageParam: page = 1, signal }) => {
			const {
				data: { data },
			} = await httpClient.get<
				SuccessPageResponse<
					PatientItem,
					{ needsReviewTotal: number; needsAttentionTotal: number }
				>
			>(`/api/v2/organizations/${scopeId}/patients-data`, {
				signal,
				params: { page, limit: PAGE_SIZE, search },
			})

			return {
				page,
				pages: data.pages,
				data: data.result,
				needsReviewTotal: data.needsReviewTotal,
				needsAttentionTotal: data.needsAttentionTotal,
			}
		},
		enabled: !!scopeId,
		placeholderData: keepPreviousData,
		initialPageParam: 1,
		select: ({ pageParams, pages }) => {
			const flatPages = pages.flat()
			const { needsReviewTotal = 0, needsAttentionTotal = 0 } =
				flatPages[0] ?? {}

			return {
				pageParams,
				pages: flatPages.flatMap(({ data }) => data),
				needsReviewTotal,
				needsAttentionTotal,
			}
		},
		getNextPageParam: ({ page, pages }) => {
			return page < pages ? page + 1 : null
		},
	})
}

export const usePatientDetails = (userId?: string) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.patientDetails, userId],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<Patient>>(
				`/api/v2/users/${userId}`,
				{
					signal,
				},
			)
		},
		staleTime: 3_600_000, // 1 hour
		enabled: !!userId,
		placeholderData: keepPreviousData,
		select({ data }) {
			return data.data
		},
	})
}

export const usePatientData = ({
	userId,
	anchorDataId,
}: {
	userId?: string
	anchorDataId?: string
}) => {
	const { httpClient } = useHttpClient()

	return useInfiniteQuery({
		queryKey: [queryKeys.patientData, userId, anchorDataId],
		queryFn: async ({ pageParam, signal }) => {
			const isAnchorFetch = pageParam === null
			const params: Record<string, any> = {
				page: isAnchorFetch ? 1 : pageParam,
				limit: PAGE_SIZE,
				order: -1,
				sort: "capturedAt",
			}

			if (isAnchorFetch && anchorDataId) {
				params.dataId = anchorDataId
			}

			const {
				data: { data },
			} = await httpClient.get<SuccessPageResponsePatientData>(
				`/api/v2/users/${userId}/data`,
				{
					signal,
					params,
				},
			)

			return {
				page: data.page,
				pages: data.pages,
				total: data.total,
				data: data.result,
				firstCaptureAt: data.firstCaptureAt,
				lastWeightKgf: data.lastWeightKgf,
				lastWeightIsUnstable: data.lastWeightIsUnstable,
				newDataPointsCount: data.newDataPointsCount,
			}
		},
		staleTime: 3_600_000, // 1 hour
		enabled: !!userId,
		placeholderData: keepPreviousData,
		initialPageParam: null as number | null,
		select: ({ pageParams, pages }) => {
			const flatPages = pages.flat()
			const {
				firstCaptureAt,
				lastWeightKgf,
				lastWeightIsUnstable,
				newDataPointsCount,
				total,
			} = flatPages[0] || {}
			return {
				total,
				pageParams,
				pages: flatPages.flatMap(({ data }) => data),
				firstCaptureAt,
				lastWeightKgf,
				lastWeightIsUnstable,
				newDataPointsCount,
			}
		},
		getNextPageParam: ({ page, pages }) => {
			return page < pages ? page + 1 : null
		},
		getPreviousPageParam: ({ page }) => {
			return page > 1 ? page - 1 : null
		},
	})
}

export const usePatientScreening = ({
	userId,
	dataId,
}: {
	userId?: string
	dataId?: string
}) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.patientScreening, userId, dataId],
		async queryFn({ signal }) {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<PatientDetailItem>>(
				`/api/v2/users/${userId}/data/${dataId}`,
				{ signal },
			)

			return data
		},
		staleTime: 3_600_000, // 1 hour
		enabled: !!userId && !!dataId,
	})
}

export const useOwnPatientData = () => {
	const { httpClient } = useHttpClient()

	return useInfiniteQuery({
		queryKey: ["patient-own-data"],
		queryFn: async ({ pageParam: page = 1, signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessPageResponse<PatientDetailItem>>(
				"/api/v1/users/own-data",
				{
					signal,
					params: { page, limit: PAGE_SIZE },
				},
			)

			return {
				page,
				pages: data.pages,
				data: data.result,
			}
		},
		placeholderData: keepPreviousData,
		initialPageParam: 1,
		select: ({ pageParams, pages }) => {
			return {
				pageParams,
				pages: pages.flatMap(({ data }) => data),
			}
		},
		getNextPageParam: ({ page, pages }) => {
			return page < pages ? page + 1 : null
		},
	})
}

export const useDeleteDataPoint = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["patient-delete-data-point", patientId],
		mutationFn: (dataId: string) => httpClient.delete(`/api/v1/data/${dataId}`),
		onSuccess: (_, dataId) => {
			queryClient.removeQueries({
				queryKey: [queryKeys.patientScreening, patientId, dataId],
			})
			queryClient.invalidateQueries({
				queryKey: [queryKeys.patientData, patientId],
				refetchType: "none",
			})
			queryClient.invalidateQueries({
				queryKey: [queryKeys.timeline, patientId],
			})
			queryClient.invalidateQueries({
				queryKey: [queryKeys.patientScreeninsStats, patientId],
			})
		},
	})
}

export const useReviewDataPoint = (patientId: string, dataId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["patient-review-data-point"],
		mutationFn: (data: TemplatedFormParams | null) =>
			httpClient.post(`/api/v1/data/${dataId}/review`, data),
		onSuccess: () => {
			const isListInFlight =
				queryClient.isFetching({
					queryKey: [queryKeys.patientData, patientId],
				}) > 0

			queryClient.setQueriesData<InfinitePatientData>(
				{ queryKey: [queryKeys.patientData, patientId] },
				(old) => {
					if (!old) {
						return old
					}

					return {
						pageParams: old.pageParams,
						pages: old.pages.map((page) => ({
							...page,
							data: page.data.map((patientData) => ({
								...patientData,
								...(patientData.id === dataId
									? { isReviewed: true, reviewedAt: today().toISOString() }
									: {}),
							})),
						})),
					}
				},
			)
			queryClient.setQueryData<PatientDetailItem>(
				[queryKeys.patientScreening, patientId, dataId],
				(old) =>
					old && {
						...old,
						isReviewed: true,
						reviewedAt: today().toISOString(),
					},
			)

			if (isListInFlight) {
				queryClient.resetQueries({
					queryKey: [queryKeys.patientData, patientId],
					predicate: ({ state }) => state.data === undefined,
				})
				queryClient.invalidateQueries({
					queryKey: [queryKeys.patientData, patientId],
				})
			}

			queryClient.invalidateQueries({
				queryKey: [queryKeys.timeline, patientId],
			})
		},
	})
}

export const usePatientScreeningsStats = ({
	patientId,
	month,
}: {
	patientId?: string
	month?: string
}) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.patientScreeninsStats, patientId, month],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<PatientDeviceDataStats>>(
				`/api/v2/users/${patientId}/screenings-stats`,
				{
					signal,
					params: {
						month,
					},
				},
			)
		},
		enabled: !!patientId,
		select({ data }) {
			return data.data
		},
	})
}

export const useStudyTemplate = (templateId?: string) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: ["patient-study-template", templateId],
		queryFn({ signal }) {
			return httpClient.get<
				SuccessResponse<{ questions: TemplatedFormQuestion[] }>
			>(`/api/v1/data/study-template/${templateId}`, {
				signal,
			})
		},
		enabled: !!templateId,
		select({ data }) {
			return data.data.questions
		},
	})
}

export const usePatientDevice = (
	patientId?: string,
	fields?: Array<keyof Device>,
) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.patientDevice, patientId, fields],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<Device | null>>(
				`/api/v1/users/${patientId}/device`,
				{
					signal,
					params: {
						fields,
					},
				},
			)
		},
		enabled: !!patientId,
		select({ data }) {
			return data.data
		},
	})
}

export const usePatientLastestScan = (patientId?: string) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.latestScan, patientId],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<PatientLatestScan>>(
				`/api/v1/users/${patientId}/latest-scan`,
				{
					signal,
				},
			)
		},
		enabled: !!patientId,
		select({ data }) {
			return data.data
		},
	})
}

export const useNurseTimes = ({
	patientId,
	startDate,
	endDate,
}: {
	patientId?: string
	startDate?: string
	endDate?: string
}) => {
	const { httpClient } = useHttpClient()
	const params = {
		startDate,
		endDate,
	}

	return useQuery({
		queryKey: [queryKeys.nurseTimes, patientId, params],
		queryFn: ({ signal }) => {
			return httpClient.get<SuccessResponse<NurseTime[]>>(
				`/api/v1/nurse-times/patients/${patientId}`,
				{
					signal,
					params,
				},
			)
		},
		enabled: !!patientId,
		placeholderData: keepPreviousData,
		select: ({ data }) =>
			data.data.toSorted(
				(a, b) => asDate(b.timestamp).unix() - asDate(a.timestamp).unix(),
			),
	})
}

export const useCreateNurseTime = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	type Payload = Pick<
		NurseTime,
		"patientId" | "durationInMinutes" | "timestamp"
	>

	return useMutation<NurseTime, ErrorResponse, Payload>({
		mutationKey: [queryKeys.nurseTimeCreate],
		mutationFn: async (nurseTime: Payload) =>
			httpClient.post("/api/v1/nurse-times", nurseTime),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.nurseTimes],
			})
		},
	})
}

export const useUpdateNurseTime = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	type Payload = Pick<NurseTime, "id" | "durationInMinutes" | "timestamp">

	return useMutation<NurseTime, ErrorResponse, Payload>({
		mutationKey: [queryKeys.nurseTimeUpdate],
		mutationFn: async (nurseTime: Payload) =>
			httpClient.patch(`/api/v1/nurse-times/${nurseTime.id}`, nurseTime),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.nurseTimes],
			})
		},
	})
}

export const useDeleteNurseTime = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: [queryKeys.nurseTimeDelete],
		mutationFn: async (id: NurseTime["id"]) =>
			httpClient.delete(`/api/v1/nurse-times/${id}`),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.nurseTimes],
			})
		},
	})
}

export const useDeviceStatusHistory = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const now = today()
	const emptyHistory = useMemo<DeviceStatusHistory>(
		() =>
			Object.fromEntries(
				Array.from({
					length: 7,
				}).map((_, index) => [
					now.subtract(index, "day").format("YYYY-MM-DD"),
					[],
				]),
			),
		[now],
	)

	return useQuery({
		queryKey: [queryKeys.patientDeviceStatusHistory, patientId],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<DeviceStatusHistory>>(
				`/api/v1/users/${patientId}/device/status-history`,
				{
					signal,
				},
			)
		},
		enabled: !!patientId,
		placeholderData: {
			data: {
				data: emptyHistory,
				status: 200,
				message: "",
				success: true,
			},
			status: 200,
			statusText: "OK",
			headers: {},
			config: {} as any,
		},
		select({ data }) {
			const history: DeviceStatusHistory = {}
			for (const day of Object.keys(emptyHistory)) {
				if (day in data.data) {
					history[day] = data.data[day]
				} else {
					history[day] = []
				}
			}

			return history
		},
	})
}

export const useProfileNote = (patientId?: string) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.profileNote, patientId],
		async queryFn({ signal }) {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<ProfileNote>>(
				`/api/v1/patients/${patientId}/profile-note`,
				{
					signal,
				},
			)

			return data
		},
		enabled: !!patientId,
		placeholderData: keepPreviousData,
	})
}

export const useUpdateProfileNote = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	type Payload = { content: string }

	return useMutation<ProfileNote, ErrorResponse, Payload>({
		mutationKey: [queryKeys.profileNoteUpdate],
		mutationFn: async (payload: Payload) => {
			const {
				data: { data },
			} = await httpClient.put<SuccessResponse<ProfileNote>>(
				`/api/v1/patients/${patientId}/profile-note`,
				payload,
			)
			return data
		},
		onSuccess: (profileNote) => {
			queryClient.setQueryData<ProfileNote>(
				[queryKeys.profileNote, patientId],
				profileNote,
			)
		},
	})
}

export const usePinMessage = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	type Payload = {
		content: string
		chatMessageId: string
		chatMessageTimestamp: string
	}

	return useMutation<ProfileNote, ErrorResponse, Payload>({
		mutationKey: [queryKeys.profileNotePinMessage],
		mutationFn: async (payload: Payload) => {
			const {
				data: { data },
			} = await httpClient.post<SuccessResponse<ProfileNote>>(
				`/api/v1/patients/${patientId}/profile-note/pin-message`,
				payload,
			)
			return data
		},
		onSuccess: (profileNote) => {
			queryClient.setQueryData<ProfileNote>(
				[queryKeys.profileNote, patientId],
				profileNote,
			)
		},
	})
}

export const useUnpinMessage = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<ProfileNote, ErrorResponse, string>({
		mutationKey: [queryKeys.profileNoteUnpinMessage],
		mutationFn: async (entryId: string) => {
			const {
				data: { data },
			} = await httpClient.delete<SuccessResponse<ProfileNote>>(
				`/api/v1/patients/${patientId}/profile-note/entries/${entryId}`,
			)
			return data
		},
		onSuccess: (profileNote) => {
			queryClient.setQueryData<ProfileNote>(
				[queryKeys.profileNote, patientId],
				profileNote,
			)
		},
	})
}

export const usePatientTimeline = ({
	patientId,
	types = [],
}: {
	patientId?: string
	types?: TimelineItem["itemType"][]
}) => {
	const { httpClient } = useHttpClient()
	const typesKey = types.join(",")
	const queryKey = useMemo(
		() => [queryKeys.timeline, patientId, typesKey],
		[patientId, typesKey],
	)
	const shouldListenToEscalations =
		types.length === 0 || types.includes("escalation")

	useInvalidateOnRealtimeEvent(["escalation_updated"], {
		queryKey,
		predicate: (data) =>
			shouldListenToEscalations && data?.patientId === patientId,
	})

	return useInfiniteQuery<
		InfinitivePage<TimelineItem>,
		Error,
		{ pages: TimelineItem[] }
	>({
		queryKey,
		queryFn: async ({ pageParam, signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessCursorResponse<TimelineItem>>(
				`/api/v1/patients/${patientId}/timeline`,
				{
					signal,
					params: {
						types,
						cursor: pageParam,
					},
				},
			)

			return {
				data: data.items,
				nextCursor: data.nextCursor,
			}
		},
		enabled: !!patientId,
		initialPageParam: undefined,
		placeholderData: keepPreviousData,
		select: ({ pages }) => {
			return {
				pages: pages.flatMap(({ data }) => data),
			}
		},
		getNextPageParam: (lastPage) => lastPage.nextCursor,
	})
}

export const usePatientScreenings = (
	patientId: string,
	dataIds: string[] = [],
) => {
	const { httpClient } = useHttpClient()

	return useQuery<TimelineScreening[], ErrorResponse>({
		enabled: dataIds.length > 0,
		placeholderData: (previousData) =>
			dataIds.length > 0 ? previousData : undefined,
		queryKey: [queryKeys.timelineScreenings, patientId, dataIds],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<TimelineScreening[]>>(
				`/api/v1/patients/${patientId}/screenings`,
				{
					signal,
					params: {
						dataIds,
					},
				},
			)

			return data
		},
		select: (data) =>
			data.toSorted(
				(a, b) => asDate(b.capturedAt).unix() - asDate(a.capturedAt).unix(),
			),
	})
}

export const useCreateTimelineNote = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	type Payload = TimelineNotePayload & { patientId: string }

	return useMutation<TimelineNote, ErrorResponse, Payload>({
		mutationKey: [queryKeys.timelineNoteCreate],
		mutationFn: async ({ patientId, ...payload }: Payload) => {
			const {
				data: { data },
			} = await httpClient.post<SuccessResponse<TimelineNote>>(
				`/api/v1/patients/${patientId}/timeline-notes`,
				payload,
			)
			return data
		},
		onSuccess: (timelineNote) => {
			queryClient.setQueriesData<{ pages: InfinitivePage<TimelineItem>[] }>(
				{ queryKey: [queryKeys.timeline, timelineNote.patientId] },
				(prev) => {
					if (!prev) {
						return prev
					}
					const item: TimelineItem = {
						itemType: "timelineNote",
						effectiveDate: timelineNote.displayDate,
						data: timelineNote,
					}
					const targetDate = asDate(item.effectiveDate)
					const targetPage =
						prev.pages.find((page) =>
							page.data.some((entry) =>
								asDate(entry.effectiveDate).isSame(targetDate, "day"),
							),
						) ?? prev.pages[0]

					return {
						...prev,
						pages: prev.pages.map((page) => {
							if (page !== targetPage) {
								return page
							}
							return {
								...page,
								data: [...page.data, item].toSorted(
									(a, b) =>
										asDate(b.effectiveDate).unix() -
										asDate(a.effectiveDate).unix(),
								),
							}
						}),
					}
				},
			)
		},
	})
}

export const useUpdateTimelineNote = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	type Payload = TimelineNotePayload & { id: string }

	return useMutation<TimelineNote, ErrorResponse, Payload>({
		mutationKey: [queryKeys.timelineNoteUpdate],
		mutationFn: async ({ id, ...payload }: Payload) => {
			const {
				data: { data },
			} = await httpClient.patch<SuccessResponse<TimelineNote>>(
				`/api/v1/timeline-notes/${id}`,
				payload,
			)
			return data
		},
		onSuccess: (timelineNote) => {
			queryClient.setQueriesData<{ pages: InfinitivePage<TimelineItem>[] }>(
				{ queryKey: [queryKeys.timeline, timelineNote.patientId] },
				(prev) => {
					if (!prev) {
						return prev
					}
					const item: TimelineItem<"timelineNote"> = {
						itemType: "timelineNote",
						data: timelineNote,
						effectiveDate: timelineNote.displayDate,
					}
					return {
						...prev,
						pages: prev.pages.map((page) => ({
							...page,
							data: page.data
								.map((entry) =>
									entry.itemType === "timelineNote" &&
									entry.data.id === item.data.id
										? item
										: entry,
								)
								.toSorted(
									(a, b) =>
										asDate(b.effectiveDate).unix() -
										asDate(a.effectiveDate).unix(),
								),
						})),
					}
				},
			)
		},
	})
}

export const useDeleteTimelineNote = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: [queryKeys.timelineNoteDelete],
		mutationFn: async ({ id }: Pick<TimelineNote, "id" | "patientId">) =>
			httpClient.delete(`/api/v1/timeline-notes/${id}`),
		onSuccess: (_, variables) => {
			queryClient.setQueriesData<{ pages: InfinitivePage<TimelineItem>[] }>(
				{ queryKey: [queryKeys.timeline, variables.patientId] },
				(prev) => {
					if (!prev) {
						return prev
					}
					return {
						...prev,
						pages: prev.pages.map((page) => ({
							...page,
							data: page.data.filter(
								({ data }) => (data as TimelineNote).id !== variables.id,
							),
						})),
					}
				},
			)
		},
	})
}

export const usePatientDiagnosisCodes = (patientId?: string) => {
	const { httpClient } = useHttpClient()

	return useQuery<PatientDiagnosisCode[], ErrorResponse>({
		queryKey: [queryKeys.patientDiagnosisCodes, patientId],
		async queryFn({ signal }) {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<PatientDiagnosisCode[]>>(
				`/api/v1/patients/${patientId}/diagnosis-codes`,
				{ signal },
			)
			return data
		},
		enabled: !!patientId,
	})
}

export const useAddPatientDiagnosisCode = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	type Payload = { code: string; description?: string }

	return useMutation<
		PatientDiagnosisCode,
		ErrorResponse,
		Payload,
		DiagnosisCodeMutationContext
	>({
		mutationKey: [queryKeys.patientDiagnosisCodeAdd, patientId],
		mutationFn: async ({ code }: Payload) => {
			const {
				data: { data },
			} = await httpClient.post<SuccessResponse<PatientDiagnosisCode>>(
				`/api/v1/patients/${patientId}/diagnosis-codes`,
				{ code },
			)
			return data
		},
		onMutate: async ({ code, description = "" }) => {
			const queryKey = [queryKeys.patientDiagnosisCodes, patientId]
			await queryClient.cancelQueries({ queryKey })
			const previousDiagnosisCodes =
				queryClient.getQueryData<PatientDiagnosisCode[]>(queryKey)

			queryClient.setQueryData<PatientDiagnosisCode[]>(
				queryKey,
				(current = []) => [
					...current,
					{
						id: `${OPTIMISTIC_ID_PREFIX}${code}`,
						patientId: patientId ?? "",
						code,
						description,
						addedBy: "",
						createdAt: today().toISOString(),
					},
				],
			)

			return { previousDiagnosisCodes }
		},
		onError: (_error, _payload, context) => {
			if (context?.previousDiagnosisCodes) {
				queryClient.setQueryData(
					[queryKeys.patientDiagnosisCodes, patientId],
					context.previousDiagnosisCodes,
				)
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.patientDiagnosisCodes, patientId],
			})
		},
	})
}

export const useRemovePatientDiagnosisCode = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<void, ErrorResponse, string, DiagnosisCodeMutationContext>(
		{
			mutationKey: [queryKeys.patientDiagnosisCodeRemove, patientId],
			mutationFn: async (diagnosisCodeId: string) => {
				await httpClient.delete(
					`/api/v1/patients/${patientId}/diagnosis-codes/${diagnosisCodeId}`,
				)
			},
			onMutate: async (diagnosisCodeId: string) => {
				const queryKey = [queryKeys.patientDiagnosisCodes, patientId]
				await queryClient.cancelQueries({ queryKey })
				const previousDiagnosisCodes =
					queryClient.getQueryData<PatientDiagnosisCode[]>(queryKey)

				queryClient.setQueryData<PatientDiagnosisCode[]>(
					queryKey,
					(current = []) =>
						current.filter(
							(diagnosisCode) => diagnosisCode.id !== diagnosisCodeId,
						),
				)

				return { previousDiagnosisCodes }
			},
			onError: (_error, _diagnosisCodeId, context) => {
				if (context?.previousDiagnosisCodes) {
					queryClient.setQueryData(
						[queryKeys.patientDiagnosisCodes, patientId],
						context.previousDiagnosisCodes,
					)
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: [queryKeys.patientDiagnosisCodes, patientId],
				})
			},
		},
	)
}
